// Pitcher planning math — rulebook × observed efficiency.
//
// - Innings forecast: how deep each pitcher can realistically go under the
//   league's pitch cap, from his own pitches-per-inning. Unique to Dugout
//   because the org pitch-count rulesets live in the product.
// - Self-inflicted runs: what share of runs against started as free passes.
// - Command score (0–99): the STATS-SIDE counterpart of the coach's 1–5
//   Control scouting grade (OVR↔xOVR pattern). Display-only, never stored,
//   completely independent of calculateOVR/calculateXOVR.
//
// Pure + framework-free. Callers resolve the team's active ruleset
// (PITCH_COUNT_RULESETS[org].ageGroups[ageGroup]) and pass it in.

export interface PitchCapRules {
  logicType?: 'threshold' | 'innings' | string;
  dailyMax?: number;
  dailyMaxInnings?: number;
}

export interface InningsForecast {
  /** Pitches-per-inning the forecast is based on */
  pIp: number;
  /** The applicable pitch cap, when the org is pitch-count based */
  capPitches: number | null;
  /** The applicable innings cap, when the org is innings based */
  capInnings: number | null;
  /** Forecast innings available in one outing (true innings, e.g. 4.67) */
  forecastIP: number | null;
}

export function forecastInnings(pIp: number | null | undefined, rules: PitchCapRules | null | undefined): InningsForecast | null {
  if (pIp == null || !(pIp > 0)) return null;
  const capPitches = rules?.dailyMax != null && rules.dailyMax < 900 ? rules.dailyMax : null;
  const capInnings = rules?.dailyMaxInnings ?? null;

  let forecastIP: number | null = capPitches != null ? capPitches / pIp : null;
  if (capInnings != null) forecastIP = forecastIP != null ? Math.min(forecastIP, capInnings) : capInnings;
  return { pIp, capPitches, capInnings, forecastIP };
}

export interface SelfInflictedRuns {
  walksScored: number;
  runsAllowed: number;
  /** 0–1 share of runs against that started as a walk */
  share: number;
}

/** From season-stats pitching buckets ({ bbs, rAllowed }). Null until both sides have data. */
export function selfInflictedRuns(pitchers: Array<{ bbs?: number; rAllowed?: number }>): SelfInflictedRuns | null {
  let bbs: number | null = null;
  let runs: number | null = null;
  pitchers.forEach(p => {
    if (p.bbs != null) bbs = (bbs ?? 0) + p.bbs;
    if (p.rAllowed != null) runs = (runs ?? 0) + p.rAllowed;
  });
  if (bbs == null || runs == null || runs === 0) return null;
  return { walksScored: bbs, runsAllowed: runs, share: bbs / runs };
}

// ── Command score ────────────────────────────────────────────────────────────
//
// Fixed youth rubric (v1 — one rubric for all age groups; revisit only with
// Dan's sign-off). Three command signals with fixed anchor ranges:
//   strike %          45% → 70%   (weight 40)
//   first-pitch strike 40% → 70%  (weight 30)
//   walks per inning   3.5 → 0.5  (weight 29, lower is better)
// Sample floor: 6 true innings — below that the score is null, not noisy.

export const COMMAND_MIN_IP = 6;

export interface CommandScore {
  score: number;           // 1–99
  sPct: number;            // 0–1 inputs echoed for the breakdown UI
  fpsPct: number;
  bbInn: number;
  ipTrue: number;
}

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

export function commandScore(p: {
  sPct?: number | null; fpsPct?: number | null; bbInn?: number | null; ipTrue?: number | null;
}): CommandScore | null {
  const { sPct, fpsPct, bbInn, ipTrue } = p;
  if (ipTrue == null || ipTrue < COMMAND_MIN_IP) return null;
  if (sPct == null || fpsPct == null || bbInn == null) return null;

  const s = clamp01((sPct - 0.45) / (0.70 - 0.45)) * 40;
  const f = clamp01((fpsPct - 0.40) / (0.70 - 0.40)) * 30;
  const b = clamp01((3.5 - bbInn) / (3.5 - 0.5)) * 29;
  const score = Math.max(1, Math.min(99, Math.round(s + f + b)));
  return { score, sPct, fpsPct, bbInn, ipTrue };
}

/**
 * Coach's 1–5 Control grade → the 1–99 scale, for the divergence caption.
 * Maps to each grade's BAND MIDPOINT (5 → 89, 4 → 69, 3 → 50, 2 → 30, 1 → 10),
 * not the band ceiling — otherwise a 5-grade becomes 99 and every real-world
 * Command score reads as "below grade", which is noise, not insight.
 */
export function controlGradeTo99(control: number | null | undefined): number | null {
  if (control == null || !(control > 0)) return null;
  return Math.round(((control - 0.5) / 5) * 99);
}
